import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  Button,
  Heading,
  Text,
  VStack,
  Spinner,
  useToast,
  Container,
  Link,
  useColorModeValue,
} from "@chakra-ui/react";
import { TiltCard } from "../common/TiltCard";
import { usePublicGlassSurface } from "../common/publicVisual";
import { useSearchParams, Link as RouterLink } from "react-router-dom";
import { hitBeApi } from "../../api/api";
import { getCookieToken } from "../../utils/utils";

/**
 * Invite links open /collaboration/join?token=... — user must be logged in with the same email/phone.
 */
export default function CollaborationJoin() {
  const glass = usePublicGlassSurface();
  const helpColor = useColorModeValue("gray.600", "gray.400");
  const okColor = useColorModeValue("gray.700", "gray.200");
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const toast = useToast();
  const [status, setStatus] = useState("idle"); // idle | loading | ok | error
  const [message, setMessage] = useState("");
  const autoAcceptStarted = useRef(false);

  useEffect(() => {
    if (!token.trim()) {
      setStatus("error");
      setMessage("Missing invite token in the link.");
      return;
    }

    async function acceptOnce() {
      if (autoAcceptStarted.current) return;
      autoAcceptStarted.current = true;
      const cookie = getCookieToken();
      if (!cookie) {
        setStatus("error");
        setMessage("Please log in first, then open your invite link again.");
        autoAcceptStarted.current = false;
        return;
      }
      setStatus("loading");
      try {
        const res = await hitBeApi("collaboration/accept", "POST", {
          token: token.trim(),
        });
        if (!res?.success) {
          throw new Error(res?.message || "Could not accept invite.");
        }
        setStatus("ok");
        setMessage(
          "You're in. Open your dashboard tabs to see shared folders or lists."
        );
        toast({
          title: "Collaboration enabled",
          status: "success",
          duration: 4000,
          isClosable: true,
        });
      } catch (e) {
        autoAcceptStarted.current = false;
        setStatus("error");
        setMessage(e?.message || "Something went wrong.");
      }
    }

    acceptOnce();
  }, [token, toast]);

  const handleRetry = () => {
    setStatus("idle");
    setMessage("");
    if (!token.trim()) {
      setStatus("error");
      setMessage("Missing invite token in the link.");
      return;
    }
    if (!getCookieToken()) {
      setStatus("error");
      setMessage("Please log in first.");
      return;
    }
    autoAcceptStarted.current = true;
    setStatus("loading");
    hitBeApi("collaboration/accept", "POST", { token: token.trim() })
      .then((res) => {
        if (!res?.success) {
          throw new Error(res?.message || "Could not accept invite.");
        }
        setStatus("ok");
        setMessage(
          "You're in. Open your dashboard tabs to see shared folders or lists."
        );
        toast({
          title: "Collaboration enabled",
          status: "success",
          duration: 4000,
          isClosable: true,
        });
      })
      .catch((e) => {
        autoAcceptStarted.current = false;
        setStatus("error");
        setMessage(e?.message || "Something went wrong.");
      });
  };

  const loginHref = `/login?next=${encodeURIComponent(
    `/collaboration/join?token=${encodeURIComponent(token.trim())}`
  )}`;

  return (
    <Box py={{ base: 12, md: 20 }} bg="transparent">
      <Container maxW="md" px={{ base: 4, md: 6 }}>
        <TiltCard tilt={8} borderRadius="2xl" borderWidth="1px" p={{ base: 6, md: 8 }} sx={{ transformStyle: "preserve-3d" }} {...glass}>
          <VStack spacing={6} align="stretch" textAlign="center">
            <Heading size="lg">Collaboration invite</Heading>
            {status === "loading" ? (
              <VStack spacing={4} py={8}>
                <Spinner size="lg" color="teal.500" />
                <Text color={helpColor}>Accepting invite…</Text>
              </VStack>
            ) : status === "ok" ? (
              <VStack spacing={4} py={4}>
                <Text color={okColor}>{message}</Text>
                <Button as={RouterLink} to="/dashboard/todos" colorScheme="teal" size="md">
                  Go to dashboard
                </Button>
              </VStack>
            ) : (
              <VStack spacing={4} py={4}>
                <Text color="red.600">{message}</Text>
                {token.trim() && !getCookieToken() ? (
                  <Button as={RouterLink} to={loginHref} colorScheme="teal">
                    Log in to continue
                  </Button>
                ) : null}
                {token.trim() && getCookieToken() ? (
                  <Button onClick={handleRetry} colorScheme="teal" variant="outline">
                    Try again
                  </Button>
                ) : null}
                <Text fontSize="sm" color={helpColor}>
                  <Link as={RouterLink} to="/" color="teal.500">
                    Home
                  </Link>
                </Text>
              </VStack>
            )}
          </VStack>
        </TiltCard>
      </Container>
    </Box>
  );
}
